import appState from "src/model/state";
import { ErrorMessage } from "src/model/error-message";
import { CartState } from "src/model/state/partials/cart.model";
import {
  CheckoutStage,
  CheckoutState,
} from "src/model/state/partials/checkout.model";
import {  getShoppingCart } from "./cart.service";
import { ShoppingCartItemItxDto } from "src/api/customer";
import { OpenapiSingleton } from "src/utils/OpenapiSingleton";
import { getBasketDefaultAddress, isAddressZoneValid, isValidAddress, validateAddress } from "src/utils/address";
import { checkUserLogged } from "src/model/utils/api-response";
import { Address } from "src/api/address";
import { forceLoginIfNeeded } from "./profile.service";
import type { Stripe, StripeElements } from "@stripe/stripe-js/types/stripe-js";
import { useShop } from "src/hooks/Utils/Shop/useShop";
import { EXPEDITION_SHIPPING_TYPE, createExpeditionBody, firstExpeditionInheritData, getFinalGroups, getSaveShippingMethodBody, getShippingMethodForType } from "src/pages/Cart/BasketUserLoged/CheckoutShipping/CheckoutShipping.utils";
import { DropPoint } from "src/api/locations";
import { ZARA_ADDRESS_URL_PARAMS } from "src/pages/ShoppingCart/models/CartShipping.model";
import { getDropPointById } from "src/hooks/React_Query/Location/useGetDropPointById";
import { refreshUrlParams } from "src/utils/BasePathTools";
import { ROUTE_PATHS } from "src/utils/constants";

type StageValidator = (
  cart: CartState,
  checkout: CheckoutState,
  stage: CheckoutStage
) => ErrorMessage | true;

const checkoutStageOrder: CheckoutStage[] = [
  "NONE",
  "CART",
  "SHIPPING",
  "PAYMENT",
  "ORDERCONFIRMATION"
];

type CheckoutBackendError = {
  parameter?: string;
  detail?: string;
};

export const getCheckoutProcessError = (
  bodyError?: CheckoutBackendError,
  fallbackMessageKey: string = "SET_SHIPPING_ADDRESS",
  extraInfo?: string,
): ErrorMessage => {
  if (bodyError?.parameter === "itemGroupItxSeller") {
    return {
      titleKey: "own_product_add_to_cart_alert_title",
      messageKey: "checkout_cart_buyer_invalid",
      extraInfo,
    };
  }

  return {
    titleKey: "CHECKOUT",
    messageKey: fallbackMessageKey,
    extraInfo,
  };
};

const checkHasShoppingCart: StageValidator = (cart, _, stage) =>
  !!cart.content || {
    titleKey: "CHECKOUT",
    messageKey: "NO_SHOPPING_CART",
  };
async function fetchDropPointData(dropPointId: string, customerId: string, marketIsoCode: string, shopMarketId: number): Promise<DropPoint | null> {

  if (!dropPointId) {
    return null;
  }

  try {
    const result = await getDropPointById(dropPointId, customerId, marketIsoCode, shopMarketId);

    if (!result) {
      return null;
    }

    const dropPoint: DropPoint = {
      datatype: result.datatype || 'droppoint',
      addressLines: result.addressLines || [],
      city: result.city || '',
      countryCode: result.countryCode || '',
      district: result.district || '',
      provinceCode: result.provinceCode || '',
      zipCode: result.zipCode || '',
      pickUpPoint: result.pickUpPoint || undefined,
      isAnonymized: result.isAnonymized || false
    };

    return dropPoint;
  } catch (err) {
    return null;
  }
}

const checkCartProducts: StageValidator = (cart, _, stage) => {
  if (cart?.hasUnavailableItems) {
    return <ErrorMessage>{
      titleKey: "cart_items_not_available_title",
      messageKey: "cart_items_not_available_subtitle",
      extraInfo: "PRODUCT_CHECK",
    };
  }
  return true;
};

const checkCartModifiedProducts: StageValidator = (cart, _, stage) => {
  if (cart?.hasChangedItems) {
    return <ErrorMessage>{
      titleKey: "payment_alert_modified_item_title",
      messageKey: "payment_alert_modified_item_description",
      extraInfo: "MODIFIED_PRODUCT_CHECK",
    };
  }
  return true;
};

export const clearUrlParams = ({ urlParams }) => {
  if ((ZARA_ADDRESS_URL_PARAMS.addressId || ZARA_ADDRESS_URL_PARAMS.expeditionId) && window.history.replaceState) {
    urlParams.delete(ZARA_ADDRESS_URL_PARAMS.expeditionId);
    urlParams.delete(ZARA_ADDRESS_URL_PARAMS.addressId);
    urlParams.delete(ZARA_ADDRESS_URL_PARAMS.returnToStage2);
    urlParams.delete(ZARA_ADDRESS_URL_PARAMS.fromAlert);
    refreshUrlParams(urlParams);
  }
}
const checkShippingStatus: StageValidator = (cart, checkout, stage) => {
  const addresses= appState.get().checkout?.addresses
  const hasValidNonBillingAddress = addresses.some(address =>
    validateAddress(address) && !address?.isBilling
  );
  const hasValidBillingAddress = !checkout.billingAddress?.isIncomplete;
  if (hasValidBillingAddress || hasValidNonBillingAddress) {
    return true
  }
  else {
    return <ErrorMessage>{
      titleKey: "CHECKOUT",
      messageKey: "NO_ADDRESS_SELECTED",
    };
  }
}
  

export const checkUserHasAddresses: StageValidator = (cart, checkout, stage) => {
  if (checkout?.addresses?.length > 0 || checkout?.billingAddress) {
    const addressData = checkout.addresses ?? [];
    const billingAddress = checkout.billingAddress;
    const allAddresses = billingAddress && !addressData.some((address) => address?.id === billingAddress?.id)
      ? [...addressData, billingAddress]
      : addressData;
    const hasSingleRestrictedAddress = allAddresses.length === 1 && !!allAddresses[0] && isAddressZoneValid(allAddresses[0]);

    const hasRestrictedBillingAddress = allAddresses.some(
      (address) => !!address?.isBilling && !address?.isIncomplete && isAddressZoneValid(address)
    );

    const hasValidNonBillingAddress = addressData.some(
      (address) => !address?.isBilling && !address?.isIncomplete && validateAddress(address)
    );
    const hasValidBillingAddressForZone = allAddresses.some(
      (address) => !!address?.isBilling && validateAddress(address)
    );

    const hasIncompleteBillingAddress = allAddresses.length > 0 && addressData.some(
      (address) => (!!address?.isBilling && address?.isIncomplete)
    );

    const hasOnlyIncompleteBillingAddress =
      allAddresses.length === 1 &&
      !!allAddresses[0]?.isBilling &&
      !!allAddresses[0]?.isIncomplete;

    const needsAddressAlert =
      hasSingleRestrictedAddress ||
      (!hasValidNonBillingAddress && !hasValidBillingAddressForZone && !hasOnlyIncompleteBillingAddress && hasRestrictedBillingAddress) ||
      (hasIncompleteBillingAddress && !hasValidNonBillingAddress) ||
      (hasRestrictedBillingAddress && !hasValidNonBillingAddress);

    if (needsAddressAlert && !hasOnlyIncompleteBillingAddress) {
      return {
        titleKey: "address_required_title",
        messageKey: "add_address_sender_alert_description",
        extraInfo: "ADDRESS_VALIDATION",
      };
    }

    const hasValidBillingAddress =
      addressData.some((address) => address?.isBilling && !address?.isIncomplete) ||
      (checkout.billingAddress && !checkout.billingAddress?.isIncomplete);

    if (!hasValidBillingAddress && !hasValidNonBillingAddress) {
      return {
        titleKey: "address_required_title",
        messageKey: "address_required_message",
        extraInfo: "ADDRESS_VALIDATION",
      };
    }

    if (hasValidBillingAddressForZone || hasValidNonBillingAddress || !hasRestrictedBillingAddress) {
      return true;
    }
  }
  return {
    titleKey: "address_required_title",
    messageKey: "address_required_message",
    extraInfo: "ADDRESS_VALIDATION",
  };
};

const stageValidations: Record<CheckoutStage, StageValidator[]> = {
  NONE: [],
  CART: [checkHasShoppingCart, checkUserHasAddresses, checkCartProducts],
  SHIPPING: [checkHasShoppingCart, checkShippingStatus],
  PAYMENT: [checkHasShoppingCart, checkCartProducts, checkCartModifiedProducts],
  ORDERCONFIRMATION:[]
};

export const updateAddressProcessing = async (address: { home: Address, droppoint: DropPoint }) => {
  const { shopMarketId, shopLang, isMarketTaxes } = useShop();
  const cartState = appState.get().cart;
  const cartCode = cartState.content.shoppingCartCode;
  const shippingAddress = address?.home?.id;
  appState.execute("CHK_SET_SHIPPING_ADDRESS", address.home);
  if (isMarketTaxes && shippingAddress) {
    const shoppingCart = await OpenapiSingleton.BuyerShoppingCartAPIApi.addAddressToId(cartCode, shippingAddress, shopMarketId, shopLang);
    appState.execute("CART_SET", shoppingCart?.data);
  }
}
export const processUrlParameters = (urlSearch: string, history: any): void => {
  const urlParams = new URLSearchParams(urlSearch);
  
  const hasReturnToStage2 = urlSearch.indexOf(ZARA_ADDRESS_URL_PARAMS.returnToStage2) !== -1;

  if (hasReturnToStage2) {
    clearUrlParams({ urlParams });
  }
};
async function processDropPointShippingMethod(
  cartGroup: any,
  customer: any,
  shopMarketId: number,
  shippingMethodsData: any[],
  expeditionAddress: any,
  sellerId: number,
  expeditions: Record<string, any>,
  result: any
): Promise<{
  isCompleted: boolean;
  shippingTypeSelected: string;
  resultValue?: any;
}> {

  // Get dropPointId from deliveryAddress field
  const dropPointId = cartGroup?.deliveryAddress;

  if (!dropPointId) {
    return {
      isCompleted: false,
      shippingTypeSelected: EXPEDITION_SHIPPING_TYPE.DROP2HOME
    };
  }

  // Try to fetch drop point data
  const dropPointData = await fetchDropPointData(
    dropPointId.toString(),
    customer?.data?.id?.toString(),
    customer?.data?.marketIsoCode,
    shopMarketId
  );

  if (!dropPointData) {
    return {
      isCompleted: false,
      shippingTypeSelected: EXPEDITION_SHIPPING_TYPE.DROP2HOME
    };
  }

  // Create expedition with drop point data
  const expedition = createExpeditionBody(
    shippingMethodsData,
    {
      home: expeditionAddress,
      droppoint: dropPointData
    },
    sellerId,
    EXPEDITION_SHIPPING_TYPE.DROPPOINT
  );

  expeditions[sellerId] = expedition;

  // Store expedition in result
  result.expedition = expedition;

  return {
    isCompleted: true,
    shippingTypeSelected: EXPEDITION_SHIPPING_TYPE.DROPPOINT,
    resultValue: result
  };
}

function findValidAddress(
  addressId: string | number,
  validAddresses: Address[],
  defaultAddress: Address,
  showWarning: boolean = true,
  sellerId?: number | string
): Address {
  if (!addressId) {
    return defaultAddress;
  }

  const foundAddress = validAddresses?.find(addr => addr.id === addressId);

  if (foundAddress) {
    return foundAddress;
  }
  
  if (showWarning) {
    appState.execute("CHK_SET_ADDRESS_ALERT_ERROR", {
      type: "ADDRESS_WARNING",
      addressId: addressId?.toString(),
      expeditionIds: sellerId ? [sellerId.toString()] : []
    });
    console.log(`Invalid address ID: ${addressId}, for seller: ${sellerId || 'unknown'}`);
  }

  return defaultAddress;
}

function getAddressFromUrlParams(
  addressIdParam: string | null,
  validAddresses: Address[],
  expeditionIds: string[] = [],
): Address | undefined {

  if (!addressIdParam) {
    appState.execute("CHK_SET_ADDRESS_ALERT_ERROR", null);
    return undefined;
  }

  const foundAddress = validAddresses?.find(
    (address) => address?.id?.toString() === addressIdParam
  );
  if (!foundAddress) {
    appState.execute("CHK_SET_ADDRESS_ALERT_ERROR", {
      type: "ADDRESS_ERROR",
      addressId: addressIdParam,
      expeditionIds: expeditionIds || []
    });
  }


  return foundAddress;
}

export const prepareShippingStage = async () => {
  if (forceLoginIfNeeded()) {
    return;
  }
  appState.execute("CHK_SET_LOADING", true);
  const { shopMarketId, shopLang, shopId, shopLangId, isMarketTaxes } = useShop();
  const cartState = appState.get().cart;
  const cartCode = cartState.content.shoppingCartCode;
  const urlParams = new URLSearchParams(window.location.search);
  const groups = Array.from(cartState.content.shoppingCartSellerItemGroupItxes || []);
  const addressIdParam = urlParams.get(ZARA_ADDRESS_URL_PARAMS.addressId);
  const expeditionIdsParam = urlParams.get(ZARA_ADDRESS_URL_PARAMS.expeditionId);
  const expeditionIds = expeditionIdsParam ? expeditionIdsParam.split(',').map(id => id.trim()) : [];

  try {
    const shippingMethodsPromise = OpenapiSingleton.BuyerShippingOptionsAPIApi.getOptions(shopMarketId, shopLang);
    const addressesPromise = OpenapiSingleton.addressUserApi.addressBook(shopId, shopLangId);
    const customerData = OpenapiSingleton.BuyerPersonalDataAPIApi.get1(shopMarketId, shopLang);
    const [methods, customer, addressBook] = await Promise.all([shippingMethodsPromise, customerData, addressesPromise]);
    const validAddresses = addressBook?.data?.addresses?.filter((address) => isValidAddress(address) && !isAddressZoneValid(address));
    const shippingAddress = getBasketDefaultAddress(validAddresses);
    const shippingAddressFromZaraValidated = getAddressFromUrlParams(addressIdParam, validAddresses,expeditionIds);
    const billingAddress=  addressBook?.data?.addresses?.find((address) => address?.isBilling);

    const data = appState.get().cart;

    let shippingMethods = {};
    let expeditions = {};
    let processedSellers = []; 

    // Step 1: For each group, fetch shipping methods
    const fetchShippingMethodsForExpedition = async (group: any) => {
      const { sellerId, sellerCountry } = group;
      if (!sellerId || !sellerCountry) return;
      const response = await OpenapiSingleton.BuyerShippingWithCustomCartAPIApi.getShippingMethods(
        shopMarketId,
        customer.data.marketIsoCode,
        sellerCountry,
        shopLang
      );
      shippingMethods[sellerId] = response?.data ?? [];
    };

    // Step 2: For each group, process drop point shipping if it applies
    const processDropPointsOnExpedition = async (group: any) => {
      const { sellerId, sellerCountry } = group;
      if (!sellerId || !sellerCountry) return;

      const cartGroupsArray = Array.isArray(data?.content?.shoppingCartSellerItemGroupItxes)
        ? data?.content?.shoppingCartSellerItemGroupItxes
        : Array.from(data?.content?.shoppingCartSellerItemGroupItxes || []);
      const cartGroup = cartGroupsArray?.find((item: any) => item?.sellerId === sellerId);

      if (cartGroup && cartGroup?.idShippingMethod) {
        const selectedMethod = shippingMethods[sellerId]?.find(
          (method: any) => method?.idShippingMethod === cartGroup?.idShippingMethod
        );
        if (selectedMethod && selectedMethod.shippingMethodType?.name?.toUpperCase() === "DROPPOINT") {
          const addressToUse = shippingAddress;
          const result = {
            sellerId,
            shippingMethods: shippingMethods[sellerId],
            expedition: null,
          };
          const dropPointResult = await processDropPointShippingMethod(
            cartGroup,
            customer,
            shopMarketId,
            shippingMethods[sellerId],
            addressToUse,
            sellerId,
            expeditions,
            result
          );
          if (dropPointResult?.isCompleted) {
            processedSellers?.push(sellerId);
          } else {
            const expedition = createExpeditionBody(
              shippingMethods[sellerId],
              { home: shippingAddress },
              sellerId,
              EXPEDITION_SHIPPING_TYPE.DROP2HOME
            );
            expeditions[sellerId] = expedition;
            processedSellers.push(sellerId);
          }
        }
      }
    };

    // Step 3: For each group, process regular shipping if not yet handled
    const processDrop2HomeForExpeditions = async (group: any) => {
      const { sellerId, sellerCountry } = group;
      if (processedSellers?.includes(sellerId)) return;
      if (!sellerId || !sellerCountry) return;

      const cartGroupsArray = Array.isArray(data?.content?.shoppingCartSellerItemGroupItxes)
        ? data?.content?.shoppingCartSellerItemGroupItxes
        : Array.from(data?.content?.shoppingCartSellerItemGroupItxes || []);
      const cartGroup = cartGroupsArray?.find((item: any) => item?.sellerId === sellerId);

      const isExpeditionInUrl = expeditionIds?.includes(sellerId?.toString());
      let addressToUse;
      if (isExpeditionInUrl && shippingAddressFromZaraValidated) {
        addressToUse = shippingAddressFromZaraValidated;
      } else {
        addressToUse = cartGroup?.deliveryAddress
          ? findValidAddress(cartGroup?.deliveryAddress, validAddresses, shippingAddress, false, sellerId)
          : shippingAddress;
      }
      const expedition = createExpeditionBody(
        shippingMethods[sellerId],
        { home: addressToUse },
        sellerId,
        EXPEDITION_SHIPPING_TYPE.DROP2HOME
      );
      expeditions[sellerId] = expedition;
      processedSellers?.push(sellerId);
    };

    // Execute all steps to initialize data
    await Promise.all(
      groups.map(async (group) => {
        await fetchShippingMethodsForExpedition(group);
        await processDropPointsOnExpedition(group);
        await processDrop2HomeForExpeditions(group);
      })
    );

    // Step 4: Final verification - make sure all seller groups have expeditions
    const allSellerIds = groups.map(group => group.sellerId);

    // Add any missing sellers with default home delivery
    allSellerIds.map(sellerId => {
      if (!expeditions[sellerId]) {
        const seller = groups?.find(g => g?.sellerId === sellerId);
        if (seller) {
          const expedition = createExpeditionBody(
            shippingMethods[sellerId] || [],
            { home: shippingAddress },
            sellerId,
            EXPEDITION_SHIPPING_TYPE.DROP2HOME
          );
          expeditions[sellerId] = expedition;
        }
      }
    });


    // Calculate national and international groups
    const customerCountry = customer?.data?.marketIsoCode || '';
    const nationalGroups = customer ? groups.filter((group) => group?.sellerCountry === customerCountry) : [];
    const internationalGroups = customer ? groups.filter((group) => group?.sellerCountry !== customerCountry) : [];
    const nationalGroupsIds = nationalGroups.map((nationalGroup) => nationalGroup?.sellerId);
    const internationalGroupsIds = internationalGroups.map((internationalGroup) => internationalGroup?.sellerId);

    // Group by route: originCountry_destinationCountry
    const routeGroups: Record<string, typeof groups> = {};
    groups.forEach((group) => {
      const originCountry = group?.sellerCountry || '';
      const destinationCountry = customerCountry;
      const routeKey = `${originCountry}_${destinationCountry}`;
      
      if (!routeGroups[routeKey]) {
        routeGroups[routeKey] = [];
      }
      routeGroups[routeKey].push(group);
    });

    // Get seller IDs grouped by route
    const routeGroupIds: Record<string, number[]> = {};
    Object.entries(routeGroups).forEach(([routeKey, routeGroupItems]) => {
      routeGroupIds[routeKey] = routeGroupItems.map((g) => g.sellerId);
    });

    // Create completed groups with shipping information and route-based grouping
    const completedGroups = groups.map((group) => {
      const isNacional = nationalGroupsIds.includes(group.sellerId);
      const dropPoint = getShippingMethodForType(EXPEDITION_SHIPPING_TYPE.DROPPOINT, shippingMethods[group.sellerId]);
      const home = getShippingMethodForType(EXPEDITION_SHIPPING_TYPE.DROP2HOME, shippingMethods[group.sellerId]);
      
      const originCountry = group?.sellerCountry || '';
      const destinationCountry = customerCountry;
      const groupKey = `${originCountry}_${destinationCountry}`;

      return {
        ...group,
        originType: isNacional ? "NACIONAL" : "INTERNACIONAL",
        originCountry,
        destinationCountry,
        groupKey,
        shippingTypesAvailable: {
          dropPoint: !!dropPoint,
          home: !!home
        },
        shippingsTypeTotalPrice: {
          dropPoint: dropPoint?.price ?? null,
          home: home?.price ?? null
        },
        sameOriginTypeGroupsIds: isNacional ? [...nationalGroupsIds] : [...internationalGroupsIds],
        sameRouteGroupsIds: routeGroupIds[groupKey] || []
      };
    });


    // Handle market taxes if needed
    if (isMarketTaxes && shippingAddress?.id && completedGroups?.length > 0) {
      const saveShippingMethodBody = getSaveShippingMethodBody({
        ...firstExpeditionInheritData(expeditions, getFinalGroups(completedGroups as any))
      });
      await OpenapiSingleton.BuyerShippingWithCustomCartAPIApi.saveShippingMethod(shopMarketId, saveShippingMethodBody, shopLang);
      const shoppingCart = await OpenapiSingleton.BuyerShoppingCartAPIApi.addAddressToId(cartCode, shippingAddress.id, shopMarketId, shopLang);
      appState.execute("CART_SET", shoppingCart?.data);
    }

    // Update app state with shipping data
    appState.execute("CHK_SET_GROUPS", completedGroups);
    appState.execute("CHK_SET_EXPEDITIONS", expeditions);
    appState.execute("CHK_SET_EXP_SHIPPING_METHODS", shippingMethods);
    appState.execute("CHK_SET_SHIPPING_METHODS", methods?.data);
    appState.execute("CHK_SET_ADDRESSES", validAddresses);
    appState.execute("CHK_SET_SHIPPING_METHOD", methods?.data[0]);
    appState.execute("CHK_SET_SHIPPING_ADDRESS", shippingAddress);
    appState.execute("CHK_SET_BILLING_ADDRESS", billingAddress);

    // Add shipping method to cart
    const shoppingCart = await OpenapiSingleton.BuyerShoppingCartAPIApi.addShippingMethodId(cartCode, methods?.data[0].id, shopMarketId, shopLang);
    appState.execute("CART_SET", shoppingCart?.data);
  } catch (e: any) {
    const bodyError = e?.response?.data;
    checkUserLogged(e);
    appState.execute("CHK_SET_ERROR", getCheckoutProcessError(bodyError, "SET_SHIPPING_METHOD"));
  } finally {
    appState.execute("CHK_SET_LOADING", false);
    clearUrlParams({ urlParams });
  }
};

const getBillingAddress = (checkout): number => {
  const shippingAddress = appState.get().checkout?.shippingAddress;
  if (shippingAddress?.id) {
    return shippingAddress.id;
  }

  const billingAddress = checkout.billingAddress;
  if (billingAddress?.id && !billingAddress.isIncomplete && !isAddressZoneValid(billingAddress)) {
    return billingAddress.id;
  }

  const firstValidAddress = (checkout?.addresses ?? []).find(
    (address) => !address?.isIncomplete && !isAddressZoneValid(address)
  );

  return firstValidAddress?.id ?? null;
};

const finishShippingStage = async () => {
  appState.execute("CHK_SET_LOADING", true);
  const { shopMarketId, shopLang } = useShop()
  const cartState = appState.get().cart;
  const cartCode = cartState.content.shoppingCartCode;
  const addressId = getBillingAddress(appState.get().checkout);

  try {
    await OpenapiSingleton.BuyerPersonalDataAPIApi.addBillingAddress(
      addressId,
      shopMarketId,
    );
    const shoppingCart =
      await OpenapiSingleton.BuyerShoppingCartAPIApi.addAddressToId(
        cartCode,
        addressId,
        shopMarketId,
        shopLang,
      );
    appState.execute("CART_SET", shoppingCart?.data);
  } catch (e: any) {
    const bodyError = e?.response?.data;
    checkUserLogged(e);
    appState.execute("CHK_SET_ERROR", getCheckoutProcessError(bodyError, "SET_SHIPPING_ADDRESS", "NOT_CONTINUE_PROCESS"));
  } finally {
    appState.execute("CHK_SET_LOADING", false);
  }
};

export const preparePaymentStage = async () => {
  if (forceLoginIfNeeded()) {
    return;
  }
  appState.execute("CHK_SET_LOADING", true);
  const { shopMarketId } = useShop()
  const cartState = appState.get().cart;
  const cartCode = cartState.content.shoppingCartCode;
  try {
    const order = await OpenapiSingleton.CustomerBuyerOrdersApi.checkOutCart(
      cartCode,
      shopMarketId,
    );
    appState.execute("CHK_SET_ORDER", order?.data);
  } catch (e: any) {
    const bodyError = e?.response?.data;
    checkUserLogged(e);
    if (bodyError.parameter === "itemItxProductState") {
      appState.execute("CHK_SET_ERROR", {
        titleKey: "cart_items_not_available_title",
        messageKey: "cart_items_not_available_subtitle",
        extraInfo: "PRODUCT_CHECK"
      });
      appState.execute("CHK_SET_LOADING", false);
    } else {
      appState.execute("CHK_SET_ERROR", getCheckoutProcessError(bodyError, "SET_SHIPPING_ADDRESS", "NOT_CONTINUE_PROCESS"));
      appState.execute("CHK_SET_LOADING", false);
    }
  } finally {
    appState.execute("CHK_SET_LOADING", false);
  }
};

export const completePayment = async (
  elements: StripeElements,
  stripe: Stripe
) => {
  await getShoppingCart(true);
  appState.execute("CHK_SET_LOADING", true);
  const { cart, checkout } = appState.get();
  const validationResult = validateStage("PAYMENT", cart, checkout);
  if (validationResult !== true) {
    // error
    appState.execute("CHK_SET_ERROR", validationResult);
    return;
  }

  const { error: submitError } = await elements.submit();
  if (submitError) {
    appState.execute("CHK_SET_ERROR", {
      titleKey: "PAYMENT",
      messageKey: "payment_generic_error_message",
      extraInfo: "PAYMENT_STRIPE_ERROR",
    });
    appState.execute("CHK_SET_LOADING", false);
    return;
  }

  const { error } = await stripe.confirmPayment({
    elements,
    clientSecret:
      appState.get().checkout.order.orderPaymentStripeDataItxDtos.clientSecret,
    redirect: "if_required",
  });

  if (error) {
    appState.execute("CHK_SET_ERROR", {
      titleKey: "PAYMENT",
      messageKey: "payment_generic_error_message",
      extraInfo: "PAYMENT_STRIPE_ERROR",
    });
    appState.execute("CHK_SET_LOADING", false);
    return;
  } else {
    appState.execute("CHK_SET_COMPLETED_STAGE", "PAYMENT");
  }
  goToCheckoutStage("ORDERCONFIRMATION");
  appState.execute("CHK_SET_LOADING", false);
};

const validateStage = (
  stage: CheckoutStage,
  cart: CartState,
  checkout: CheckoutState
) => {
  const validators = stageValidations[stage];
  const result = validators.map((validator) => validator(cart, checkout, stage)).find((result) => result !== true);
  return result ?? true;
};

const prepareOperations: Record<CheckoutStage, () => Promise<void>> = {
  NONE: null,
  CART: () => getShoppingCart(true),
  SHIPPING: prepareShippingStage,
  PAYMENT: preparePaymentStage,
  ORDERCONFIRMATION: null
};

const completeOperations: Record<CheckoutStage, () => Promise<void>> = {
  NONE: null,
  CART: null,
  SHIPPING: finishShippingStage,
  PAYMENT: null,
  ORDERCONFIRMATION: null
};

export const acceptItemChanges = async (item: ShoppingCartItemItxDto) => {
  appState.execute("CHK_SET_LOADING", true);
  const { shopMarketId, shopLang } = useShop()
  const cartState = appState.get().cart;
  const cartCode = cartState.content.shoppingCartCode;
  try {
    const cart =
      await OpenapiSingleton.BuyerShoppingCartAPIApi.acceptCartChanges1(
        cartCode,
        item.productItx.id,
        shopMarketId,
        shopLang,
      );
    appState.execute("CART_SET", cart?.data);
  } catch (e: any) {
    checkUserLogged(e);
    appState.execute("CHK_SET_ERROR", {
      titleKey: "ACCEPT_CART_CHANGES",
      messageKey: "ACCEPT_CART_CHANGES",
    });
  }

  appState.execute("CHK_SET_LOADING", false);
};

export const acceptItemChangesPayment = async (successActionCallback?: () => void) => {
  appState.execute("CHK_SET_LOADING", true);
  const { shopMarketId, shopLang } = useShop()
  let cart = appState.get().cart.content;
  try {
      const response = await OpenapiSingleton.BuyerShoppingCartAPIApi.acceptCartChanges(
          cart.shoppingCartCode,
          shopMarketId,
          shopLang
      );
      cart = response?.data
      appState.execute("CART_SET", cart);
      appState.execute("CART_CHECK_FINISHED", undefined);
      successActionCallback && successActionCallback();
      appState.execute("CHK_SET_LOADING", false);
  } catch (e: any) {
      if (e?.response?.data) {
          checkUserLogged(e);
      }
      appState.execute("CART_CHECK_PRODUCT_ERROR", {
          messageKey: "ACCEPT_CART_CHANGES",
          titleKey: "ACCEPT_CART_CHANGES"
      });
  }
}

export const setShippingAddress = (address: Address) => appState.execute("CHK_SET_SHIPPING_ADDRESS", address);

export const getStageOrder = (stage: CheckoutStage) =>
  checkoutStageOrder.findIndex((s) => s === stage);

export const getNextStage = (stage: CheckoutStage) =>
  checkoutStageOrder[getStageOrder(stage) + 1];

export const getPreviousStage = (stage: CheckoutStage) =>
  checkoutStageOrder[getStageOrder(stage) - 1];

export const goToCheckoutStage = async (stage: CheckoutStage) => {
  const currentStage = appState.get().checkout.stage;
  if (stage === currentStage) {
    return;
  }

  appState.execute("CHK_SET_LOADING", true);
  const stageOrder = getStageOrder(stage);

  if (getStageOrder(currentStage) < stageOrder) {
    // check whether we can complete the current stage
    // update shopping cart
    if(stage !== "CART" && stage !== "ORDERCONFIRMATION") await getShoppingCart(true);
    const { cart, checkout } = appState.get();
    const validationResult = validateStage(currentStage, cart, checkout);
    if (validationResult !== true) {
      // error
      appState.execute("CHK_SET_ERROR", validationResult);
      return;
    } else {
      // finish the current stage
      const finishOperation = completeOperations[currentStage];
      await finishOperation?.();
      if (appState.get().checkout.error) {
        // stop if error
        return;
      } else {
        // mark stage as completed
        appState.execute("CHK_SET_COMPLETED_STAGE", currentStage);
        appState.execute("CHK_SET_STAGE", stage)
      }
    }
  }

  // check whether the previous stage is completed
  const completedStageOrder = getStageOrder(appState.get().checkout.completedStage);
  const redirectOtherStage = new URLSearchParams(location.search).has("returnToStage2");
  
  if (completedStageOrder < stageOrder - 1 && !redirectOtherStage) {
    appState.execute("CHK_SET_STAGE", "CART");
    appState.execute("CHK_SET_COMPLETED_STAGE", "NONE");
    appState.execute("CHK_SET_ERROR", { messageKey: "INVALID_STAGE" });
    return;
  }

  // move to the desired stage
  appState.execute("CHK_SET_STAGE", stage);
  // prepare new stage
  const prepareOperation = prepareOperations[stage];
  await prepareOperation?.();
  appState.execute("CHK_SET_LOADING", false);
};

export const dismissCheckoutError = () =>
  appState.execute("CHK_DISMISS_ERROR", undefined);

export const loadConfirmedOrder = async (orderCode: string) => {
  appState.execute("CHK_SET_LOADING", true);
  const { shopMarketId } = useShop()
  try {
    const order = await OpenapiSingleton.CustomerBuyerOrdersApi.getOrderByCode(
      orderCode,
      shopMarketId,
    );
    if (order?.data.state === "UNABLE_TO_COMPLETE") {
      appState.execute("CHK_SET_ERROR", {
        titleKey: "ORDER_NOT_PROCEED",
        messageKey: "ITEM_NOT_AVAILABLE_FOR_PURCHASE",
      });
      appState.execute("CNF_SET_ORDER", order?.data);
      appState.execute("CHK_SET_LOADING", false);
      return false;
    } else {
      appState.execute("CNF_SET_ORDER", order?.data);
      appState.execute("CHK_SET_LOADING", false);
      return true;
    }
  } catch (e: any) {
    checkUserLogged(e);
    appState.execute("CNF_SET_ERROR", {
      titleKey: "ORDER_CONFIRMATION",
      messageKey: "FETCH_ORDER",
    });
    appState.execute("CHK_SET_LOADING", false);
    return false;
  }
};

export const checkoutReset = () => {
  appState.execute("CHK_RESET", undefined);
};
